import { Artwork } from "@/types/artworksData";
import { getExtensionFromUrl, ModelLoadSource } from "../../utils/general.utils";
import { DEFAULT_SOUND_MODEL_URL } from "./artwork.constants";
import type { RuntimeMediaDelivery, RuntimeMediaFamily } from "@/utils/runtimeConfig";
import { resolveRuntimeMediaUrl } from "../../../../utils/runtimeMedia";

export type RuntimeAssetVariant = "original" | "optimized" | "frame";

export type ArtworkModelSourceOptions = {
  useOptimizedAssets?: boolean;
  mediaDelivery?: RuntimeMediaDelivery | null;
};

const SHA256_PATTERN = /^[a-f0-9]{64}$/;
export const resolveRuntimeArtworkMediaUrl = (
  value: string | null | undefined,
  family: RuntimeMediaFamily,
  delivery?: RuntimeMediaDelivery | null,
  manifest?: Artwork["artworkData"]["runtimeMedia"],
): string | null => {
  return resolveRuntimeMediaUrl(value, family, delivery, manifest);
};

const artworkMediaFamily = (artwork: Artwork): RuntimeMediaFamily | null => {
  if (artwork.type === "image") return "image";
  if (artwork.type === "sound") return "audio";
  if (artwork.type === "video") return "video";
  if (artwork.type === "3dmodel" || artwork.type === "diorama") return "model3d";
  return null;
};

export const resolveArtworkForRuntimeMedia = (
  artwork: Artwork,
  delivery?: RuntimeMediaDelivery | null,
): Artwork => {
  const family = artworkMediaFamily(artwork);
  if (!family) return artwork;
  const dataUrl = resolveRuntimeArtworkMediaUrl(
    artwork.artworkData?.dataUrl,
    family,
    delivery,
    artwork.artworkData?.runtimeMedia,
  );
  if (!dataUrl || dataUrl === artwork.artworkData?.dataUrl) return artwork;
  return {
    ...artwork,
    artworkData: {
      ...artwork.artworkData,
      dataUrl,
    },
  };
};

const isSha256 = (value: unknown): value is string => (
  typeof value === "string" && SHA256_PATTERN.test(value)
);

const isPositiveInteger = (value: unknown): value is number => (
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value > 0
);

const isImmutableHttpsUrl = (value: unknown): value is string => {
  if (typeof value !== "string" || !value) return false;

  try {
    const url = new URL(value);
    return url.protocol === "https:" &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash;
  } catch (_error) {
    return false;
  }
};

export const resolveOptimizedArtworkModelUrl = (
  activeArtwork: Artwork,
  options: ArtworkModelSourceOptions = {}
): string | null => {
  if (!options.useOptimizedAssets) return null;

  const runtime3d = activeArtwork?.artworkData?.runtime3d;
  const source = runtime3d?.source;
  const optimized = runtime3d?.optimized;
  const safety = runtime3d?.safety;

  if (!source || !optimized || !safety) return null;
  if (optimized.status !== "approved") return null;
  if (safety.status !== "safe" || safety.sourceSha256Matched !== true) return null;
  if (source.contentType !== "model/gltf-binary") return null;
  if (!isSha256(source.sha256) || !isPositiveInteger(source.byteLength)) return null;
  if (optimized.contentType !== "model/gltf-binary") return null;
  if (!isSha256(optimized.sha256) || !isPositiveInteger(optimized.byteLength)) return null;
  if (
    optimized.compression?.draco !== true ||
    optimized.compression.meshopt !== false ||
    optimized.compression.ktx2 !== false
  ) return null;
  if (!isImmutableHttpsUrl(optimized.url)) return null;

  return optimized.url;
};

export const resolveArtworkModelSource = (
  activeArtwork: Artwork,
  artworkFrameUrl: string,
  options: ArtworkModelSourceOptions = {}
): { source: ModelLoadSource, imageDioaram: string, assetVariant: RuntimeAssetVariant } => {
  const explicitModelSource = activeArtwork.artworkData.modelSource;
  if (explicitModelSource) {
    return {
      source: explicitModelSource,
      imageDioaram: activeArtwork.type === "3dmodel" ? "3dmodel" : "diorama",
      assetVariant: "original",
    };
  }

  switch (activeArtwork.type) {
    case "diorama":
    case "3dmodel": {
      const optimizedModelUrl = resolveOptimizedArtworkModelUrl(activeArtwork, options);
      if (optimizedModelUrl) {
        return {
          source: {
            type: "glb",
            url: optimizedModelUrl,
          },
          imageDioaram: activeArtwork.type === "3dmodel" ? "3dmodel" : "diorama",
          assetVariant: "optimized",
        };
      }

      const runtimeModelUrl = resolveRuntimeArtworkMediaUrl(
        activeArtwork.artworkData.dataUrl,
        "model3d",
        options.mediaDelivery,
        activeArtwork.artworkData.runtimeMedia,
      );
      const extension = getExtensionFromUrl(runtimeModelUrl);
      const sourceUrl = runtimeModelUrl || "";
      const lowerSourceUrl = sourceUrl.toLowerCase();
      const isUnsupported3DSource = Boolean(
        !sourceUrl ||
        lowerSourceUrl.includes("/embed") ||
        lowerSourceUrl.includes("/player") ||
        lowerSourceUrl.endsWith(".html") ||
        lowerSourceUrl.endsWith(".htm") ||
        ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension || "")
      );
      const isDirect3DSource = activeArtwork.type === "3dmodel"
        ? !isUnsupported3DSource
        : Boolean(extension && ["glb", "gltf", "fbx", "obj", "dae", "ply", "3ds", "zip"].includes(extension));
      if (isDirect3DSource) {
        return {
          source: runtimeModelUrl,
          imageDioaram: activeArtwork.type === "3dmodel" ? "3dmodel" : "diorama",
          assetVariant: "original",
        };
      }

      return {
        source: artworkFrameUrl,
        imageDioaram: "artwork",
        assetVariant: "frame",
      };
    }
    case "sound":
      return {
        source: DEFAULT_SOUND_MODEL_URL,
        imageDioaram: "artwork",
        assetVariant: "frame",
      };
    case "video":
    case "image":
    default:
      return {
        source: artworkFrameUrl,
        imageDioaram: "artwork",
        assetVariant: "frame",
      };
  }
};

export const resolveArtworkRuntimeAssetUrls = (
  activeArtwork: Artwork,
  artworkFrameUrl: string,
  options: ArtworkModelSourceOptions = {}
) => {
  const modelSource = resolveArtworkModelSource(activeArtwork, artworkFrameUrl, options);
  const runtimeArtwork = resolveArtworkForRuntimeMedia(activeArtwork, options.mediaDelivery);
  const shouldCacheDisplayUrl =
    activeArtwork.type === "image" ||
    activeArtwork.type === "video" ||
    (activeArtwork.type !== "sound" && modelSource.imageDioaram === "artwork");

  return {
    modelSource: modelSource.source,
    displayUrl: shouldCacheDisplayUrl ? runtimeArtwork.artworkData.dataUrl : null,
    displayKind: activeArtwork.type === "video" ? "video" as const : "image" as const,
    soundUrl: activeArtwork.type === "sound" ? runtimeArtwork.artworkData.dataUrl : null,
    imageDioaram: modelSource.imageDioaram,
    assetVariant: modelSource.assetVariant,
  };
};
